import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useModals from '../Modal/manager/useModal';
import { useNavigate } from 'react-router-dom';
import { usePaymentState } from 'payment_state/PaymentStateContext';
import { checkEmail, checkNumber, isEmpty } from 'utils/Utils';
import TermsModal from '../Modal/TermsModal';
import PrivacyPolicyModal from '../Modal/PrivacyPolicyModal';
import { isMobile } from 'react-device-detect';
import IframeBridge from 'IframeBridge';
import Keypad from '../keypad';
import CardFormErrorModal from '../Modal/CardFormErrorModal';
import ErrorModal from '../Modal/ErrorModal';
import { CreditKeyinRequest } from '../TabCredit/types';
import { useMutation } from 'react-query';
import { innerSearch, paymentCredit } from 'api/Api';
import LoadingIndicator from '../LoadingIndicator';
import ArrowPng from 'app/assets/image/common/arrow.png';
import { useMediaQuery } from 'react-responsive';

interface KeyinFormProps {
  keypadToken: string;
  keyinType: string;
}

const KeyinForm = ({ keypadToken, keyinType }: KeyinFormProps) => {
  const refs = {
    firstKeypadRef: useRef<HTMLDivElement>(null),
    secondKeypadRef: useRef<HTMLDivElement>(null),
    expiryDateKeypadRef: useRef<HTMLDivElement>(null),
    lastKeypadRef: useRef<HTMLDivElement>(null),
    tab1Ref: useRef<HTMLDivElement>(null),
  };
  const foucsTerms = useRef<HTMLDivElement>(null);
  const { t, i18n } = useTranslation();
  const { openModal } = useModals();
  const navigate = useNavigate();
  const { paymentState, setPaymentState } = usePaymentState();

  const [loading, setLoading] = useState<boolean>(false);
  const [isNameEdit, setNameEdit] = useState<boolean>(false);
  const [email, setEmail] = useState<string>('');
  const [familyName, setFamilyName] = useState<string>('');
  const [identityType, setIdentityType] = useState<'birth' | 'business'>(
    'birth',
  );
  const [name, setName] = useState<string>('');
  const [cardNumber, setCardNumber] = useState<string>('');
  const [isCardNumberError, setCardNumberError] = useState<boolean>(false);
  const [isNoIdentifyNoError, setNoIdentifyNoError] = useState<boolean>(false);
  const [isNoPasswordError, setIsNoPasswordError] = useState<boolean>(false);
  const [isNoNameError, setNoNameError] = useState<boolean>(false);
  const [isLastCardNumberError, setLastCardNumberError] =
    useState<boolean>(false);
  const [isExpirationDateError, setExpirationDateError] =
    useState<boolean>(false);
  const [agreeCheck, setAgreeCheck] = useState<boolean>(false);
  const [AgreeCheckError, setAgreeCheckError] = useState<boolean>(false);

  const [encryptedFirstCardNumber, setEncryptedFirstCardNumber] = useState({
    data: null,
    hmac: null,
    coordType: 'PIN',
    shuffleType: 'RANDOM',
    encryptionType: 'AES128',
    length: 0,
  });

  const [encryptedSecondCardNumber, setEncryptedSecondCardNumber] = useState({
    data: null,
    hmac: null,
    coordType: 'PIN',
    shuffleType: 'RANDOM',
    encryptionType: 'AES128',
    length: 0,
  });

  const [encodedExpiryDateMonth, setEncodedExpiryDateMonth] = useState({
    data: null,
    hmac: null,
    coordType: 'PIN',
    shuffleType: 'RANDOM',
    encryptionType: 'AES128',
    length: 0,
  });
  const [encodedExpiryDateYear, setEncodedExpiryDateYear] = useState({
    data: null,
    hmac: null,
    coordType: 'PIN',
    shuffleType: 'RANDOM',
    encryptionType: 'AES128',
    length: 0,
  });

  const [encodedIdentityNumber, setEncodedIdentityNumber] = useState({
    data: null,
    hmac: null,
    coordType: 'PIN',
    shuffleType: 'RANDOM',
    encryptionType: 'AES128',
    length: 0,
  });

  const [encodedPassword, setEncodedPassword] = useState({
    data: null,
    hmac: null,
    coordType: 'PIN',
    shuffleType: 'RANDOM',
    encryptionType: 'AES128',
    length: 0,
  });

  const [isEncryptedFirstCardNumberError, setEncryptedFirstCardNumberError] =
    useState<boolean>(false);
  const [isEncryptedSecondCardNumberError, setEncryptedSecondCardNumberError] =
    useState<boolean>(false);
  const [lastCardNumber, setLastCardNumber] = useState<string>('');
  const [open, setOpen] = useState<boolean>(false);
  const isMobileSize = useMediaQuery({ maxWidth: 767 });

  // 결제 결과 PaymentState set 및 결과화면 처리
  const payResult = data => {
    // 결과상태
    const resultStatus = String(data.data.response.resultStatus).toLowerCase();
    // InnerSearch 결과 값 PaymentState 설정
    setPaymentState(prevState => ({
      ...prevState,
      resultCode: data.data.response.resultCode,
      resultMessage: data.data.response.resultMessage,
      tid: data.data.response.tid,
      orderId: data.data.response.orderId,
      mallReserved: data.data.response.mallReserved,
      returnUrl:
        data.data.returnUrl !== null
          ? data.data.returnUrl
          : paymentState.returnUrl,
      descriptor: data.data.display.descriptor,
      billKey: data.data.response.billKey,
      schemeGroup: data.data.response.schemeGroup,
      schemeCode: data.data.response.schemeCode,
    }));
    // Status에 따른 Paging 처리
    switch (resultStatus) {
      case 'success':
        navigate('/success');
        break;
      case 'decline':
        navigate('/decline');
        break;
      case 'processing':
        navigate('/processing');
        break;
      case 'pending':
        navigate('/pending');
        break;
      case 'failed':
        navigate('/failed');
        break;
      default:
        navigate('/error');
    }
  };

  // 2D 결제 실행
  const Credit2DPay = useMutation(
    (keyinRequest: CreditKeyinRequest) => paymentCredit(keyinRequest),
    {
      onSuccess: data => {
        // 결제 처리용 TID Setting
        setPaymentState(prevState => ({
          ...prevState,
          tid: data.data.tid,
        }));
        // InnerSearch 실행
        transactResult.mutate(data.data.tid);
      },
      onError: error => {
        setLoading(false);
        navigate('/error');
      },
    },
  );

  // InnerSearch 실행
  const transactResult = useMutation((tid: string) => innerSearch(tid), {
    onSuccess: data => {
      setLoading(false);
      // InnerSearch 결과처리
      payResult(data);
      // 결과 상태가 없을경우 예외처리
      if (
        data.data.response.resultStatus === null ||
        data.data.response.resultStatus === undefined
      ) {
        navigate('/error');
      }
    },
    onError: error => {
      setLoading(false);
      navigate('/error');
    },
  });

  const onChangeCardNumber = e => {
    const value = e.target.value;
    if (value.length === 0) {
      setCardNumber('');
      return;
    }
    //숫자 외 입력 못하도록 변경
    const isNumber = checkNumber(value.charAt(value.trim().length - 1));
    if (isNumber) {
      setCardNumber(e.target.value);
    }

    if (value.length === 4) {
      const nextRef = document.querySelectorAll('div.input.firstKeypad')[0]
        .children[1] as HTMLElement;
      if (nextRef) {
        nextRef.focus();
        nextRef.click();
      }
    }
  };

  const focusCardNumber = e => {
    if (refs.tab1Ref.current) {
      refs.tab1Ref.current.scrollTop = 150;
    }
  };

  const onChangeLastCardNumber = useCallback(
    e => {
      const value = e.target.value;
      if (value.length === 0) {
        setLastCardNumber('');
        return;
      }
      //숫자 외 입력 못하도록 변경
      const isNumber = checkNumber(value.charAt(value.trim().length - 1));
      if (isNumber) {
        setLastCardNumber(e.target.value);
      }

      if (value.length === 6) {
        const nextRef = document.querySelectorAll(
          'div.input.expiryDateMonthKeyin',
        )[0].children[1] as HTMLElement;
        if (nextRef) {
          nextRef.focus();
          nextRef.click();
        }
        // if (refs.tab1Ref.current) {
        //   refs.tab1Ref.current.scrollTop = 200;
        // }
      }
    },
    [lastCardNumber],
  );

  const handleTermsOrPrivacy = content => {
    if (content === 'SERVICE') {
      openModal(TermsModal, {
        onSubmit: () => {},
      });
    } else {
      openModal(PrivacyPolicyModal, {
        onSubmit: () => {},
      });
    }
  };

  const openInputError = () => {
    openModal(CardFormErrorModal, {
      error: t('card_input_error'),
      message: t('card_input_error_msg'),
    });
  };

  const clickPay = async e => {
    e.preventDefault();

    // 이름, 성 체크
    if (isEmpty(familyName) || isEmpty(name)) {
      setNoNameError(true);
      openInputError();
      return;
    }
    setNoNameError(false);

    if (cardNumber.length < 4) {
      setCardNumberError(true);
      openInputError();
      return;
    }
    setCardNumberError(false);

    if (encryptedFirstCardNumber.length < 4) {
      setEncryptedFirstCardNumberError(true);
      openInputError();
      return;
    }

    setEncryptedFirstCardNumberError(false);

    if (encryptedSecondCardNumber.length < 4) {
      setEncryptedSecondCardNumberError(true);
      openInputError();
      return;
    }

    setEncryptedSecondCardNumberError(false);

    if (lastCardNumber.length < 3 || lastCardNumber.length > 6) {
      setLastCardNumberError(true);
      openInputError();
      return;
    }

    setLastCardNumberError(false);

    if (encodedExpiryDateMonth.length < 2 || encodedExpiryDateYear.length < 2) {
      setExpirationDateError(true);
      openInputError();
      return;
    }

    setExpirationDateError(false);

    if (keyinType.includes('I')) {
      if (encodedIdentityNumber.length < 6) {
        setNoIdentifyNoError(true);
        openModal(ErrorModal, {
          message: t('error_identityNo_check'),
        });
        return;
      }
    }
    if (keyinType.includes('P')) {
      if (encodedPassword.length < 1) {
        setIsNoPasswordError(true);
        openModal(ErrorModal, {
          message: t('error_password_check'),
        });
        return;
      }
    }

    // 이메일 validation
    if (email !== '') {
      if (!checkEmail(email)) {
        openModal(ErrorModal, {
          message: t('check_email_format'),
        });
        return;
      }
    }

    if (!agreeCheck) {
      setAgreeCheckError(true);
      openModal(ErrorModal, {
        message: t('error_agree_check'),
      });
      foucsTerms.current?.scrollIntoView({ behavior: 'smooth' });
      return;
    }

    const keyinRequest: CreditKeyinRequest = {
      reqDate: paymentState.reqDate,
      orderId: paymentState.orderId,
      productName: paymentState.productName,
      currency: paymentState.requestCurrency,
      amount: paymentState.requestAmount,
      schemeCode: 'KEYIN',
      cardNumber: {
        firstCardNumber: cardNumber,
        encSecondCardNumber: encryptedFirstCardNumber,
        encThirdCardNumber: encryptedSecondCardNumber,
        lastCardNumber: lastCardNumber,
      },
      cardMonth: encodedExpiryDateMonth,
      cardYear: encodedExpiryDateYear,
      keypadToken: keypadToken,
      cardHolder: `${familyName} ${name}`,
      email: email,
      sign: paymentState.sign,
      displayType: 'UI',
      mallReserved: paymentState.mallReserved,
      webhookUrl: paymentState.webhookUrl,
      returnUrl: paymentState.returnUrl,
      billkeyReq: paymentState.billkeyReq,
      ...(keyinType.includes('I') && { identityNumber: encodedIdentityNumber }),
      ...(keyinType.includes('P') && { cardPassword: encodedPassword }),
    };
    setLoading(true);
    try {
      await Credit2DPay.mutate(keyinRequest);
    } catch (error: unknown) {
      console.error('Axios Error', error);
      navigate('/error');
    }
  };

  const handleKeyDown = e => {
    if (e.key === 'Enter') {
      e.preventDefault(); // Enter 키의 기본 동작을 중지시킴
    }
  };

  const handleAllClose = e => {
    e.preventDefault();

    const returnObject = {
      resultStatus: 'FAILED',
      resultCode: 'FAIL',
      resultMessage: 'Unprocessed payment.',
      tid: paymentState.tid,
      orderId: paymentState.orderId,
      mallReserved: paymentState.mallReserved,
      returnUrl: paymentState.returnUrl,
      schemeGroup: paymentState.schemeGroup,
      schemeCode: paymentState.schemeCode,
    };

    if (isMobile) {
      const url = new URL(paymentState.returnUrl);
      const params = new URLSearchParams(url.search);

      params.set('resultStatus', 'FAILED');
      params.set(
        'resultCode',
        paymentState.resultCode ? paymentState.resultCode : '',
      );
      params.set(
        'resultMessage',
        paymentState.resultMessage ? paymentState.resultMessage : '',
      );
      params.set('tid', paymentState.tid);
      params.set('orderId', paymentState.orderId);
      params.set(
        'mallReserved',
        paymentState?.mallReserved ? paymentState.mallReserved : '',
      );
      params.set(
        'schemeGroup',
        paymentState?.schemeGroup ? paymentState.schemeGroup : '',
      );
      params.set(
        'schemeCode',
        paymentState?.schemeCode ? paymentState.schemeCode : '',
      );
      window.location.href = url + '?' + params;
    } else {
      window.sessionStorage.removeItem('auth');
      window.sessionStorage.removeItem('mid');
      window.sessionStorage.removeItem('clientKey');
      IframeBridge.sendViewOff(returnObject);
    }
  };

  useEffect(() => {
    setEncodedIdentityNumber({
      data: null,
      hmac: null,
      coordType: 'PIN',
      shuffleType: 'RANDOM',
      encryptionType: 'AES128',
      length: 0,
    });
  }, [identityType]);

  return (
    <>
      {loading || !keyinType ? (
        <div
          style={{
            display: 'flex',
            height: isMobileSize ? 'calc(100% - 180px)' : 'calc(100% - 270px)',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <LoadingIndicator />
        </div>
      ) : (
        <>
          <div className="b-section">
            <div className="tab">
              <div
                ref={refs.tab1Ref}
                id="tab-1"
                className="tab-on current"
                style={
                  isMobileSize
                    ? { height: 'calc((var(--vh, 1vh) * 100) - 300px)' }
                    : { height: 310 }
                }
              >
                <div className="form first">
                  <div className="title">{t('card_holder_title')}</div>
                  <ul className="depth-2">
                    <li>
                      <div className="sub-title">
                        {t('first_name')}
                        <span className="redstar">*</span>
                      </div>
                      <div className={isNoNameError ? 'input error' : 'input'}>
                        <input
                          name="text"
                          type="text"
                          onChange={e => {
                            setNameEdit(true);
                            setName(e.target.value);
                          }}
                          value={name}
                        />
                      </div>
                    </li>
                    <li>
                      <div className="sub-title">
                        {t('last_name')}
                        <span className="redstar">*</span>
                      </div>
                      <div className={isNoNameError ? 'input error' : 'input'}>
                        <input
                          name="text"
                          type="text"
                          onChange={e => {
                            setNameEdit(true);
                            setFamilyName(e.target.value);
                          }}
                          value={familyName}
                        />
                      </div>
                    </li>
                  </ul>
                </div>
                <div className="form">
                  <ul className="depth-1">
                    <li
                      className="input"
                      style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        marginBottom: 10,
                      }}
                    ></li>
                    <div
                      style={{
                        fontSize: '14px',
                        color: '#8a9db4',
                        marginBottom: '5px',
                      }}
                      className="sub-title"
                    >
                      {t('card_number')}
                      <span className="redstar">*</span>
                    </div>
                    <li style={{ display: 'flex', flexWrap: 'wrap' }}>
                      <div
                        className={isCardNumberError ? 'input error' : 'input'}
                        style={{ width: '22%', float: 'left', order: 1 }}
                      >
                        <a></a>
                        <input
                          id="card"
                          name="text"
                          type="text"
                          maxLength={4}
                          placeholder={'0000'}
                          onChange={onChangeCardNumber}
                          onFocus={focusCardNumber}
                          value={cardNumber}
                          //   ref={refs.cardNumberRef}
                        />
                      </div>
                      <Keypad
                        identifier={'firstKeypad'}
                        // cardNumber={cardNumber}
                        setStateFunction={setEncryptedFirstCardNumber}
                        isError={isEncryptedFirstCardNumberError}
                        setError={setEncryptedFirstCardNumberError}
                        textLength="4"
                        placeholderText="0000"
                        css={{
                          width: '22%',
                          float: 'left',
                          marginLeft: '4%',
                          order: '2',
                        }}
                        keypadToken={keypadToken}
                      />
                      <Keypad
                        identifier={'secondKeypad'}
                        setStateFunction={setEncryptedSecondCardNumber}
                        isError={isEncryptedSecondCardNumberError}
                        setError={setEncryptedSecondCardNumberError}
                        textLength="4"
                        placeholderText="0000"
                        css={{
                          width: '22%',
                          float: 'left',
                          marginLeft: '4%',
                          order: '3',
                        }}
                        keypadToken={keypadToken}
                      />
                      <div
                        className={
                          isLastCardNumberError
                            ? 'input lastCardNumber error'
                            : 'input lastCardNumber'
                        }
                        style={{
                          width: '22%',
                          float: 'left',
                          marginLeft: '4%',
                          order: 4,
                        }}
                      >
                        <a></a>
                        <input
                          id="lastCardNumber"
                          name="text"
                          type="text"
                          maxLength={6}
                          minLength={3}
                          placeholder={'000000'}
                          onChange={onChangeLastCardNumber}
                          value={lastCardNumber}
                          //   ref={refs.lastKeypadRef}
                        />
                      </div>
                      {(isCardNumberError || isLastCardNumberError) && (
                        <div style={{ order: 5 }} className="error-text">
                          {t('invalid_card_error_text')}
                        </div>
                      )}
                    </li>
                  </ul>
                  <ul className="depth-2">
                    <li>
                      <div className="sub-title">
                        {t('expiration_date')}
                        <span className="redstar">*</span>
                      </div>
                      <div style={{ display: 'flex' }}>
                        <Keypad
                          identifier={'expiryDateMonthKeyin'}
                          setStateFunction={setEncodedExpiryDateMonth}
                          placeholderText="MM"
                          textLength="2"
                          isError={isExpirationDateError}
                          setError={setExpirationDateError}
                          css={{ width: '45%' }}
                          // ref={expiryDateKey}
                          keypadToken={keypadToken}
                        />
                        <Keypad
                          identifier={'expiryDateYearKeyin'}
                          setStateFunction={setEncodedExpiryDateYear}
                          placeholderText="YY"
                          textLength="2"
                          isError={isExpirationDateError}
                          setError={setExpirationDateError}
                          css={{ width: '45%', marginLeft: '10%' }}
                          // ref={expiryDateKey}
                          keypadToken={keypadToken}
                        />
                      </div>
                    </li>
                    <li></li>
                  </ul>
                  {keyinType.includes('I') && (
                    <>
                      <ul className="depth-2">
                        <li style={{ width: '100%' }}>
                          <div className="sub-title">
                            {t('keyinIdentity')}
                            <span className="redstar">*</span>
                          </div>
                          <div
                            style={{
                              display: 'flex',
                              gap: 5,
                              padding: '5px 0px',
                            }}
                          >
                            <input
                              type="radio"
                              name="cardSelect"
                              value={'birth'}
                              checked={identityType === 'birth'}
                              onChange={e => setIdentityType('birth')}
                            />
                            {t('birthday')}
                            <input
                              type="radio"
                              name="cardSelect"
                              value={'business'}
                              checked={identityType === 'business'}
                              onChange={e => setIdentityType('business')}
                            />
                            {t('businessNumber')}
                          </div>
                        </li>
                        <li style={{ width: '50%' }}>
                          <div
                            className={
                              isNoIdentifyNoError ? 'input error' : 'input'
                            }
                          >
                            <Keypad
                              identifier={'identityNumber'}
                              type={identityType}
                              setStateFunction={setEncodedIdentityNumber}
                              placeholderText={
                                identityType === 'birth'
                                  ? '000000'
                                  : '0000000000'
                              }
                              textLength={identityType === 'birth' ? '6' : '10'}
                              isError={isNoIdentifyNoError}
                              setError={setNoIdentifyNoError}
                              css={{ width: '100%' }}
                              keypadToken={keypadToken}
                            />
                          </div>
                        </li>
                        <li></li>
                      </ul>
                    </>
                  )}
                  {keyinType.includes('P') && (
                    <ul className="depth-2">
                      <li>
                        <div className="sub-title">
                          {t('password')}
                          <span className="redstar">*</span>
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'row',
                            width: '100%',
                            height: '100%',
                            gap: 5,
                            alignItems: 'center',
                          }}
                        >
                          <Keypad
                            identifier={'keypadPassword'}
                            setStateFunction={setEncodedPassword}
                            placeholderText="**"
                            textLength="2"
                            isError={isNoPasswordError}
                            setError={setIsNoPasswordError}
                            css={{ width: '25%' }}
                            // ref={expiryDateKey}
                            keypadToken={keypadToken}
                          />
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              height: '100%',
                            }}
                          >
                            {'**'}
                          </div>
                        </div>
                      </li>
                    </ul>
                  )}
                </div>
                <div className="mailing">
                  <div className="title">{t('payment_confirm_email_opt')}</div>
                  <div className="input">
                    <input
                      // ref={refs.emailRef}
                      name="email"
                      type="email"
                      onChange={e => setEmail(e.target.value)}
                      value={email}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="agreeFloatBox">
              <div className="label" ref={foucsTerms}>
                <div className="check">
                  <input
                    type="checkbox"
                    id={AgreeCheckError ? 'checkbox error' : 'checkbox'}
                    name="agree"
                    checked={agreeCheck}
                    onChange={e => {
                      setAgreeCheck(e.target.checked);
                    }}
                  />

                  <label
                    htmlFor={AgreeCheckError ? 'checkbox error' : 'checkbox'}
                  ></label>
                </div>
                <div className="columnBox">
                  <div className="text">
                    <div
                      onClick={() => setAgreeCheck(!agreeCheck)}
                      style={{ cursor: 'pointer' }}
                    >
                      {t('start_agree_terms')}
                      {t('terms_and_conditions')}
                      {t('and_the')}
                      {t('privacy_policy')}
                      {t('end_agree_terms')}
                    </div>
                    <img
                      className={`arrow ${open ? 'open' : ''}`}
                      src={ArrowPng}
                      alt=""
                      onClick={() => setOpen(!open)}
                    />
                  </div>
                  <div className={`text toggleRow ${open ? 'open' : ''}`}>
                    <p className="dot">●</p>
                    <span
                      className="link"
                      onClick={() => handleTermsOrPrivacy('SERVICE')}
                    >
                      {t('terms_and_conditions')}
                    </span>
                  </div>
                  <div className={`text toggleRow ${open ? 'open' : ''}`}>
                    <p className="dot">●</p>
                    <span
                      className="link"
                      onClick={() => handleTermsOrPrivacy('PRIVACY')}
                    >
                      {t('privacy_policy')}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div className="c-section">
              <div className="box">
                <button className="all-close" onClick={handleAllClose}>
                  {t('close')}
                </button>
                <button
                  className="orange"
                  onClick={clickPay}
                  onKeyDown={e => handleKeyDown(e)}
                >
                  {t('pay_now')}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default KeyinForm;
